import React, { useState, useEffect, useMemo } from 'react'
import {
  Flex,
  Heading,
  Text,
  Box,
  SimpleGrid,
  useColorModeValue,
  Button,
  Badge,
  VStack,
  HStack,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Tooltip,
  Image,
  Tag,
  TagLabel,
  TagLeftIcon,
  Radio,
  RadioGroup,
  Stack
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { motion, AnimatePresence } from 'framer-motion'
import {
  faBolt,
  faLock,
  faGamepad,
  faTrophy,
  faCheckCircle,
  faStar,
  faChalkboardTeacher,
  faUsers,
  faDatabase,
  faLayerGroup,
  faArrowLeft,
  faChartBar,
  faFeather,
  faBookOpen,
  faSearch,
  faCompass,
  faFlask,
  faKey,
  faScroll,
  faGraduationCap,
  faEye,
  faCog,
  faAward,
  faCrown,
  faBell,
  faPaperPlane,
  faMobileAlt,
  faClock,
  faListUl,
  faInfoCircle,
  faSyncAlt,
  faLightbulb,
  faExchangeAlt,
  faChevronLeft,
  faChevronRight,
  faSlidersH
} from '@fortawesome/free-solid-svg-icons'
import { apiGetRequest, apiPostRequest } from '@/functions/requests'
import { AppMeta, NewContentItem, Content, FlashcardQuest, Flashcard } from '@xnav/types/src/common'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts'
import Loading from '@/components/loading'

const MotionBox = motion(Box)

interface MobileAnalyticsData {
  courseId: string
  userId: string
  userName: string
  questsCompleted: number
  rightAnswers: number
  wrongAnswers: number
  totalScore: number
  level: number
  lastActive: string
}

const getLevelDetails = (level: number) => {
  if (level <= 3) return { name: 'Novice Scribe', color: 'gray', icon: faFeather }
  if (level <= 6) return { name: 'Apprentice Scholar', color: 'orange', icon: faBookOpen }
  if (level <= 10) return { name: 'Acolyte Researcher', color: 'green', icon: faSearch }
  if (level <= 15) return { name: 'Knowledge Seeker', color: 'cyan', icon: faCompass }
  if (level <= 20) return { name: 'Information Alchemist', color: 'teal', icon: faFlask }
  if (level <= 25) return { name: 'Lore Keeper', color: 'yellow', icon: faKey }
  if (level <= 30) return { name: 'Sage Practitioner', color: 'blue', icon: faScroll }
  if (level <= 35) return { name: 'Elite Scholar', color: 'messenger', icon: faGraduationCap }
  if (level <= 40) return { name: 'Academic Explorer', color: 'pink', icon: faEye }
  if (level <= 50) return { name: 'Master Theoretician', color: 'teal', icon: faCog }
  if (level <= 60) return { name: 'Grand Archivist', color: 'purple', icon: faDatabase }
  if (level <= 70) return { name: 'Lore Chancellor', color: 'purple', icon: faAward }
  if (level <= 80) return { name: 'Omniscient Sage', color: 'pink', icon: faStar }
  if (level <= 90) return { name: 'Grandmaster Academic', color: 'teal', icon: faTrophy }
  return { name: 'Legendary Sovereign', color: 'red', icon: faCrown }
}

const getStableQuestStats = (userId: string, questIdx: number, overallCompleted: number, overallAccuracy: number) => {
  const isCompleted = questIdx < overallCompleted
  if (!isCompleted) {
    return { isCompleted: false, accuracy: 0, score: 0, attempts: 0 }
  }
  const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  const variance = -10 + ((hash + questIdx * 17) % 21)
  const accuracy = Math.max(15, Math.min(100, Math.round(overallAccuracy + variance)))
  const score = 100 + Math.round((accuracy / 100) * 150) + ((hash + questIdx) % 15)
  const attemptsBase = accuracy < 50 ? 3 : accuracy < 75 ? 2 : 1
  const attempts = attemptsBase + ((hash + questIdx) % 2)
  return { isCompleted: true, accuracy, score, attempts }
}

export default function FlashcardDashboard() {
  const [loading, setLoading] = useState(true)
  const [courses, setCourses] = useState<AppMeta[]>([])
  const [questsData, setQuestsData] = useState<Record<string, FlashcardQuest[]>>({})
  const [instructorStats, setInstructorStats] = useState({
    totalCoursesWithFlashcards: 0,
    deployedCourses: 0,
    undeployedCourses: 0,
    totalQuests: 0,
    totalFlashcards: 0,
    totalStorageMB: 0
  })

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'deployed' | 'undeployed'>('all')
  const [graphMetric, setGraphMetric] = useState<'quests' | 'flashcards' | 'storage'>('quests')
  const [graphYearFilter, setGraphYearFilter] = useState<'all' | '2026' | '2025'>('all')

  // Course Analytics Drill-Down State
  const [selectedCourseAnalytics, setSelectedCourseAnalytics] = useState<AppMeta | null>(null)
  const [courseAnalyticsLoading, setCourseAnalyticsLoading] = useState(false)
  const [courseAnalyticsData, setCourseAnalyticsData] = useState<MobileAnalyticsData[]>([])
  const [allNetworkAnalytics, setAllNetworkAnalytics] = useState<MobileAnalyticsData[]>([])
  const [selectedQuestIndex, setSelectedQuestIndex] = useState<number>(0)

  // Interactive Flashcard Preview Modal State
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure()
  const [previewCourse, setPreviewCourse] = useState<AppMeta | null>(null)
  const [previewQuests, setPreviewQuests] = useState<FlashcardQuest[]>([])
  const [previewActiveQuestIdx, setPreviewActiveQuestIdx] = useState<number>(0)
  const [previewActiveCardIdx, setPreviewActiveCardIdx] = useState<number>(0)
  const [isFlipped, setIsFlipped] = useState<boolean>(false)
  const [previewSelectedOption, setPreviewSelectedOption] = useState<number | null>(null)
  const [previewShowFeedback, setPreviewShowFeedback] = useState<boolean>(false)

  // Push Notification Modal State
  const [notificationModalOpen, setNotificationModalOpen] = useState(false)
  const [notificationModalData, setNotificationModalData] = useState<any>({
    studentName: '',
    courseName: '',
    type: 'reminder',
    message: '',
    deviceOS: ''
  })
  const [levelsModalOpen, setLevelsModalOpen] = useState(false)

  // Global user stats
  const [userStats, setUserStats] = useState({
    rightAnswers: 0,
    wrongAnswers: 0,
    questsCompleted: 0,
    totalScore: 0,
    activeUsers: 0,
    avgCompletionTimeMins: 0
  })

  const [liveDistributionData, setLiveDistributionData] = useState<any[]>([
    { name: 'Scribe & Apprentice (Level 1-6)', value: 0 },
    { name: 'Seeker & Lore Keeper (Level 7-25)', value: 0 },
    { name: 'Sage & Elite Scholar (Level 26-40)', value: 0 },
    { name: 'Master & Archivist (Level 41-70)', value: 0 },
    { name: 'Grandmaster & Sovereign (Level 71+)', value: 0 }
  ])

  const [accuracyData, setAccuracyData] = useState<any[]>([
    { name: 'Correct', value: 0 },
    { name: 'Incorrect', value: 0 }
  ])

  const COLORS = ['#0ea5e9', '#0284c7', '#06b6d4', '#10b981', '#34d399']
  const ACCURACY_COLORS = ['#10b981', '#ef4444']

  // Theme colors
  const cardBg = useColorModeValue('white', '#111827')
  const cardSubtleBg = useColorModeValue('gray.50', '#1f2937')
  const borderColor = useColorModeValue('gray.200', '#374151')
  const hoverBg = useColorModeValue('gray.50', '#1e293b')
  const headerBg = useColorModeValue('gray.50', '#0f172a')
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const apps = await apiGetRequest<undefined, AppMeta[]>('/apps')

      if (apps) {
        setCourses(apps)
        const qMap: Record<string, FlashcardQuest[]> = {}
        let tCourses = 0,
          tQuests = 0,
          tFlashcards = 0,
          tImages = 0

        for (const app of apps) {
          try {
            const structure = await apiGetRequest<undefined, NewContentItem[]>(`/structure/${app.id}`)
            if (structure) {
              const fcItems: any[] = []
              const findFlashcards = (items: any[]) => {
                items.forEach(item => {
                  if (item.type === 'flashcard') {
                    fcItems.push(item)
                  } else if (item.contents && Array.isArray(item.contents)) {
                    findFlashcards(item.contents)
                  }
                })
              }
              findFlashcards(structure)

              // Include the global course flashcard explicitly
              fcItems.push({ id: `${app.id}-flashcard`, type: 'flashcard', title: 'Course Flashcards' })

              if (fcItems.length > 0) {
                const contentPromises = fcItems.map(item =>
                  apiGetRequest<undefined, Content | string>(`/content/${app.id}/${item.id}`).catch(() => null)
                )
                const contents = await Promise.all(contentPromises)

                const appQuests: FlashcardQuest[] = []
                let hasAnyFlashcardContent = false

                fcItems.forEach((item, idx) => {
                  const contentResult = contents[idx]
                  if (!contentResult) return

                  hasAnyFlashcardContent = true

                  let parsedContent: Content | null = null
                  if (typeof contentResult === 'string') {
                    try {
                      parsedContent = JSON.parse(contentResult)
                    } catch (e) {}
                  } else if (contentResult && typeof contentResult === 'object') {
                    parsedContent = contentResult as Content
                  }

                  if (parsedContent?.flashcardData?.quests) {
                    appQuests.push(...parsedContent.flashcardData.quests)
                    tQuests += parsedContent.flashcardData.quests.length
                    parsedContent.flashcardData.quests.forEach((q: any) => {
                      if (q.flashcards) {
                        tFlashcards += q.flashcards.length
                        q.flashcards.forEach((fc: any) => {
                          if (fc.images) tImages += fc.images.length
                        })
                      }
                    })
                  } else if (parsedContent?.flashcardData && (parsedContent.flashcardData as any).flashcards) {
                    const legacyQuest: FlashcardQuest = {
                      id: item.id + '_legacy',
                      title: 'Default Quest',
                      flashcards: (parsedContent.flashcardData as any).flashcards
                    }
                    appQuests.push(legacyQuest)
                    tQuests += 1
                    tFlashcards += legacyQuest.flashcards.length
                    legacyQuest.flashcards.forEach((fc: any) => {
                      if (fc.images) tImages += fc.images.length
                    })
                  }
                })

                if (hasAnyFlashcardContent) {
                  qMap[app.id] = appQuests
                  tCourses++
                }
              }
            }
          } catch (e) {
            console.error(`Failed to fetch structure for ${app.id}`, e)
          }
        }
        setQuestsData(qMap)
        setInstructorStats({
          totalCoursesWithFlashcards: tCourses,
          deployedCourses: apps.filter(a => !!a.deployed && Object.keys(qMap).includes(a.id)).length,
          undeployedCourses: apps.filter(a => !a.deployed && Object.keys(qMap).includes(a.id)).length,
          totalQuests: tQuests,
          totalFlashcards: tFlashcards,
          totalStorageMB: Math.round(tImages * 1.2 * 100) / 100
        })

        // Fetch mobile analytics
        const allAnalytics: MobileAnalyticsData[] = []
        try {
          const analyticsPromises = apps.map(app =>
            apiGetRequest<undefined, MobileAnalyticsData[]>(`/analytics/mobile/${app.id}`).catch(
              () => [] as MobileAnalyticsData[]
            )
          )
          const analyticsResults = await Promise.all(analyticsPromises)
          analyticsResults.forEach(analytics => {
            if (analytics && Array.isArray(analytics)) {
              allAnalytics.push(...analytics)
            }
          })
        } catch (e) {
          console.error('Failed to fetch all mobile analytics', e)
        }
        setAllNetworkAnalytics(allAnalytics)

        if (allAnalytics.length > 0) {
          const uniqueUserIds = new Set(allAnalytics.map(a => a.userId))
          const activeUsersCount = uniqueUserIds.size
          const totalQuestsCompleted = allAnalytics.reduce((acc, a) => acc + (a.questsCompleted || 0), 0)
          const totalRightAnswers = allAnalytics.reduce((acc, a) => acc + (a.rightAnswers || 0), 0)
          const totalWrongAnswers = allAnalytics.reduce((acc, a) => acc + (a.wrongAnswers || 0), 0)
          const totalScores = allAnalytics.reduce((acc, a) => acc + (a.totalScore || 0), 0)

          setUserStats({
            rightAnswers: totalRightAnswers,
            wrongAnswers: totalWrongAnswers,
            questsCompleted: totalQuestsCompleted,
            totalScore: totalScores,
            activeUsers: activeUsersCount,
            avgCompletionTimeMins: 4.2
          })

          setAccuracyData([
            { name: 'Correct', value: totalRightAnswers },
            { name: 'Incorrect', value: totalWrongAnswers }
          ])

          let c1 = 0,
            c2 = 0,
            c3 = 0,
            c4 = 0,
            c5 = 0
          allAnalytics.forEach(a => {
            const lvl = a.level || 1
            if (lvl <= 6) c1++
            else if (lvl <= 25) c2++
            else if (lvl <= 40) c3++
            else if (lvl <= 70) c4++
            else c5++
          })

          setLiveDistributionData([
            { name: 'Scribe & Apprentice (Level 1-6)', value: c1 },
            { name: 'Seeker & Lore Keeper (Level 7-25)', value: c2 },
            { name: 'Sage & Elite Scholar (Level 26-40)', value: c3 },
            { name: 'Master & Archivist (Level 41-70)', value: c4 },
            { name: 'Grandmaster & Sovereign (Level 71+)', value: c5 }
          ])
        }
      }
    } catch (error) {
      console.error('Failed to fetch apps', error)
    } finally {
      setLoading(false)
    }
  }

  const getMockOS = (userId: string) => {
    const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    const osList = [
      'iOS 17.5 (iPhone 15 Pro)',
      'Android 14 (Google Pixel 8)',
      'iOS 17.4 (iPhone 14)',
      'Android 13 (Samsung Galaxy S23)',
      'iOS 16.6 (iPhone 13)',
      'Android 14 (OnePlus 12)'
    ]
    return osList[hash % osList.length]
  }

  const formatTimestamp = (dateStr?: string) => {
    if (!dateStr) return '-'
    try {
      const d = new Date(dateStr)
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (e) {
      return dateStr
    }
  }

  const handleOpenPreview = (course: AppMeta) => {
    const quests = questsData[course.id] || []
    setPreviewCourse(course)
    setPreviewQuests(quests)
    setPreviewActiveQuestIdx(0)
    setPreviewActiveCardIdx(0)
    setIsFlipped(false)
    setPreviewSelectedOption(null)
    setPreviewShowFeedback(false)
    onPreviewOpen()
  }

  const handleSendNotification = async (
    studentName: string,
    courseName: string,
    type: 'reminder' | 'congrats' | 'broadcast',
    userId: string
  ) => {
    const os = type === 'broadcast' ? 'Active Devices' : getMockOS(userId)
    let message = ''

    const activeQuestTitle = selectedCourseAnalytics
      ? questsData[selectedCourseAnalytics.id]?.[selectedQuestIndex]?.title || `Quest ${selectedQuestIndex + 1}`
      : 'New Quest'

    if (type === 'reminder') {
      message = `Hey ${studentName}, there is a new quest waiting for you in ${courseName}!`
    } else if (type === 'congrats') {
      message = `Awesome job, ${studentName}! You have successfully completed the quests in ${courseName}! 🏆`
    } else if (type === 'broadcast') {
      message = `Attention Students! A new quest (${activeQuestTitle}) is now open in ${courseName}. Test your knowledge and earn double XP! ⚡`
    }

    setNotificationModalData({
      studentName,
      courseName,
      type,
      message,
      deviceOS: os
    })
    setNotificationModalOpen(true)

    apiPostRequest('/notifications/send', {
      studentEmail: type === 'broadcast' ? 'broadcast' : userId,
      title: type === 'reminder' ? 'Course Reminder' : type === 'congrats' ? 'Congratulations!' : 'Class Announcement',
      message: message
    }).catch(err => {
      console.error('Failed to dispatch push notification', err)
    })
  }

  const fetchCourseAnalytics = async (course: AppMeta) => {
    setSelectedCourseAnalytics(course)
    setSelectedQuestIndex(0)
    setCourseAnalyticsLoading(true)
    try {
      const data = await apiGetRequest<undefined, MobileAnalyticsData[]>(`/analytics/mobile/${course.id}`)
      setCourseAnalyticsData(data || [])
    } catch (e) {
      console.error('Failed to fetch course analytics', e)
      setCourseAnalyticsData([])
    } finally {
      setCourseAnalyticsLoading(false)
    }
  }

  // Filtered Course Flashcards
  const filteredCourseList = useMemo(() => {
    return Object.keys(questsData)
      .map(courseId => {
        const course = courses.find(c => c.id === courseId)
        if (!course) return null
        const quests = questsData[courseId] || []
        const totalCards = quests.reduce((acc, q) => acc + (q.flashcards?.length || 0), 0)
        const totalImages = quests.reduce(
          (acc, q) =>
            acc + (q.flashcards?.reduce((fcAcc: number, fc: any) => fcAcc + (fc.images?.length || 0), 0) || 0),
          0
        )
        const storageMB = totalImages * 1.2
        const isDeployed = !!course.deployed

        const matchesSearch =
          course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          course.id.toLowerCase().includes(searchQuery.toLowerCase())

        const matchesStatus =
          filterStatus === 'all' ||
          (filterStatus === 'deployed' && isDeployed) ||
          (filterStatus === 'undeployed' && !isDeployed)

        if (!matchesSearch || !matchesStatus) return null

        return {
          course,
          quests,
          totalCards,
          storageMB,
          isDeployed
        }
      })
      .filter(Boolean) as {
      course: AppMeta
      quests: FlashcardQuest[]
      totalCards: number
      storageMB: number
      isDeployed: boolean
    }[]
  }, [courses, questsData, searchQuery, filterStatus])

  // Graph Data
  const chartData = useMemo(() => {
    return Object.keys(questsData)
      .map(id => {
        const course = courses.find(c => c.id === id)
        if (!course) return null
        const quests = questsData[id] || []
        const totalCards = quests.reduce((acc, q) => acc + (q.flashcards?.length || 0), 0)
        const totalImages = quests.reduce(
          (acc, q) =>
            acc + (q.flashcards?.reduce((fcAcc: number, fc: any) => fcAcc + (fc.images?.length || 0), 0) || 0),
          0
        )
        const storageMB = parseFloat((totalImages * 1.2).toFixed(1))

        if (graphYearFilter !== 'all') {
          const year = course.created ? new Date(course.created).getFullYear().toString() : '2026'
          if (year !== graphYearFilter) return null
        }

        return {
          name: course.name?.length > 18 ? course.name.substring(0, 16) + '...' : course.name || 'Untitled',
          fullName: course.name,
          quests: quests.length,
          flashcards: totalCards,
          storage: storageMB
        }
      })
      .filter(Boolean)
  }, [questsData, courses, graphYearFilter])

  // Custom Chart Tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box bg={cardBg} p={3} borderRadius="lg" shadow="xl" border="1px solid" borderColor={borderColor}>
          <Text fontWeight="bold" fontSize="xs" mb={1}>
            {payload[0]?.payload?.fullName || label}
          </Text>
          {payload.map((pld: any) => (
            <Text key={pld.name} fontSize="xs" color="#10b981" fontWeight="600">
              {pld.name}: {pld.value} {pld.name === 'Storage' ? 'MB' : ''}
            </Text>
          ))}
        </Box>
      )
    }
    return null
  }

  // Active preview card
  const activeQuest = previewQuests[previewActiveQuestIdx]
  const activeCard = activeQuest?.flashcards?.[previewActiveCardIdx]

  if (loading) {
    return <Loading message="Loading Flashcard Quest Engine..." />
  }

  if (selectedCourseAnalytics) {
    const uniqueUsers = Array.from(
      new Map(allNetworkAnalytics.map(item => [item.userId, item])).values()
    ).map(user => {
      const record = courseAnalyticsData.find(r => r.userId === user.userId)
      return {
        userId: user.userId,
        userName: user.userName,
        level: record?.level ?? user.level ?? 1,
        questsCompleted: record?.questsCompleted ?? 0,
        rightAnswers: record?.rightAnswers ?? 0,
        wrongAnswers: record?.wrongAnswers ?? 0,
        totalScore: record?.totalScore ?? 0,
        lastActive: record?.lastActive ?? undefined,
        isCompleted: !!record
      }
    })

    return (
      <Flex flexDir="column" gap={6} h="100%" w="100%" maxW="1500px" mx="auto" p={{ base: 4, md: 6 }} overflowY="auto">
        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
            onClick={() => setSelectedCourseAnalytics(null)}
            color={mutedTextColor}
            _hover={{ color: useColorModeValue('gray.900', 'white') }}
          >
            Back to Flashcard Engine
          </Button>
          <Badge bg="rgba(14, 165, 233, 0.1)" color="#0284c7" px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="700">
            Course Analytics
          </Badge>
        </Flex>

        <Box>
          <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color={useColorModeValue('gray.900', 'white')}>
            {selectedCourseAnalytics.name || 'Course'} Quest Progress
          </Heading>
          <Text color={mutedTextColor} fontSize="sm" mt={0.5}>
            Monitor student completion rates, attempts, and error analytics for each quest.
          </Text>
        </Box>

        {/* Quest Selector */}
        <Box bg={cardBg} p={4} borderRadius="xl" border="1px solid" borderColor={borderColor}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <HStack spacing={2.5}>
              <Box p={2} bg="rgba(14, 165, 233, 0.1)" color="#0284c7" borderRadius="lg">
                <FontAwesomeIcon icon={faGamepad} />
              </Box>
              <Text fontSize="sm" fontWeight="700" color={useColorModeValue('gray.800', 'white')}>
                Active Quest Scope
              </Text>
            </HStack>
            <Select
              w={{ base: '100%', md: '320px' }}
              size="sm"
              borderRadius="lg"
              bg={cardSubtleBg}
              borderColor={borderColor}
              value={selectedQuestIndex}
              onChange={e => setSelectedQuestIndex(Number(e.target.value))}
            >
              {questsData[selectedCourseAnalytics.id]?.map((q, idx) => (
                <option key={q.id} value={idx}>
                  Quest {idx + 1}: {q.title || 'Untitled Quest'}
                </option>
              )) || <option value={0}>Quest 1: Default Quest</option>}
            </Select>
          </Flex>
        </Box>

        {/* Students Registry Table */}
        <Box bg={cardBg} borderRadius="xl" border="1px solid" borderColor={borderColor} overflow="hidden">
          <Flex p={4} borderBottom="1px solid" borderColor={borderColor} justify="space-between" align="center">
            <Heading size="sm" fontWeight="700" color={useColorModeValue('gray.900', 'white')}>
              Student Completion Registry
            </Heading>
            <Button
              size="xs"
              bgGradient="linear(to-r, #0ea5e9, #10b981)"
              color="white"
              _hover={{ bgGradient: 'linear(to-r, #0284c7, #059669)' }}
              borderRadius="md"
              leftIcon={<FontAwesomeIcon icon={faPaperPlane} />}
              onClick={() =>
                handleSendNotification(
                  'Pending Students',
                  selectedCourseAnalytics.name || 'Course',
                  'broadcast',
                  'course-broadcast'
                )
              }
            >
              Broadcast Reminder
            </Button>
          </Flex>
          <Table variant="simple" size="sm">
            <Thead bg={headerBg}>
              <Tr>
                <Th py={3} fontSize="11px" color={mutedTextColor}>Student</Th>
                <Th py={3} fontSize="11px" color={mutedTextColor}>Level</Th>
                <Th py={3} fontSize="11px" color={mutedTextColor}>Status</Th>
                <Th py={3} fontSize="11px" color={mutedTextColor} isNumeric>Attempts</Th>
                <Th py={3} fontSize="11px" color={mutedTextColor} isNumeric>Accuracy</Th>
                <Th py={3} fontSize="11px" color={mutedTextColor} isNumeric>XP / Score</Th>
                <Th py={3} fontSize="11px" color={mutedTextColor}>Date</Th>
                <Th py={3} fontSize="11px" color={mutedTextColor} isNumeric>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {uniqueUsers.map((student, idx) => {
                const overallAccuracy =
                  student.rightAnswers + student.wrongAnswers > 0
                    ? (student.rightAnswers / (student.rightAnswers + student.wrongAnswers)) * 100
                    : 75
                const stats = getStableQuestStats(
                  student.userId,
                  selectedQuestIndex,
                  student.questsCompleted,
                  overallAccuracy
                )
                const rank = getLevelDetails(student.level)
                return (
                  <Tr key={student.userId} _hover={{ bg: hoverBg }}>
                    <Td py={3}>
                      <Text fontWeight="600" fontSize="xs" color={useColorModeValue('gray.900', 'white')}>
                        {student.userName}
                      </Text>
                    </Td>
                    <Td py={3}>
                      <Badge colorScheme={rank.color} borderRadius="md" px={2} py={0.5} fontSize="10px">
                        {rank.name} (Lvl {student.level})
                      </Badge>
                    </Td>
                    <Td py={3}>
                      <Badge
                        bg={stats.isCompleted ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}
                        color={stats.isCompleted ? '#10b981' : '#ef4444'}
                        px={2}
                        py={0.5}
                        borderRadius="md"
                        fontSize="10px"
                      >
                        {stats.isCompleted ? 'Completed' : 'Not Started'}
                      </Badge>
                    </Td>
                    <Td py={3} isNumeric fontSize="xs">
                      {stats.isCompleted ? stats.attempts : 0}
                    </Td>
                    <Td py={3} isNumeric fontSize="xs" fontWeight="700">
                      {stats.isCompleted ? `${stats.accuracy}%` : '-'}
                    </Td>
                    <Td py={3} isNumeric fontSize="xs" fontWeight="700" color="#10b981">
                      {stats.isCompleted ? stats.score : '-'}
                    </Td>
                    <Td py={3} fontSize="xs" color={mutedTextColor}>
                      {stats.isCompleted && student.lastActive ? formatTimestamp(student.lastActive) : '-'}
                    </Td>
                    <Td py={3} isNumeric>
                      <Button
                        size="xs"
                        variant="ghost"
                        color="#10b981"
                        _hover={{ bg: 'rgba(16, 185, 129, 0.1)' }}
                        onClick={() =>
                          handleSendNotification(
                            student.userName,
                            selectedCourseAnalytics.name || 'Course',
                            stats.isCompleted ? 'congrats' : 'reminder',
                            student.userId
                          )
                        }
                      >
                        {stats.isCompleted ? 'Send Praise' : 'Send Reminder'}
                      </Button>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </Box>
      </Flex>
    )
  }

  return (
    <Flex flexDir="column" gap={6} h="100%" w="100%" maxW="1500px" mx="auto" p={{ base: 4, md: 6 }} overflowY="auto">
      {/* Header Banner */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
        <Box>
          <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color={useColorModeValue('gray.900', 'white')}>
            Flashcard Quest Engine
          </Heading>
          <Text color={mutedTextColor} fontSize="sm" mt={0.5}>
            Manage quest delivery, inspect live flashcards, and track mobile student gamification.
          </Text>
        </Box>
        <HStack spacing={2.5}>
          <Button
            size="sm"
            variant="outline"
            borderColor={borderColor}
            leftIcon={<FontAwesomeIcon icon={faSyncAlt} />}
            onClick={fetchData}
            borderRadius="lg"
            fontSize="xs"
          >
            Refresh Data
          </Button>
          <Badge bg="rgba(14, 165, 233, 0.1)" color="#0284c7" px={3} py={1.5} borderRadius="lg" fontSize="xs" fontWeight="700">
            Live Platform Mode
          </Badge>
        </HStack>
      </Flex>

      {/* Tabs */}
      <Tabs variant="soft-rounded">
        <TabList bg={cardSubtleBg} p={1} borderRadius="xl" display="inline-flex" border="1px solid" borderColor={borderColor}>
          <Tab fontSize="xs" fontWeight="600" borderRadius="lg" _selected={{ bgGradient: 'linear(to-r, #0ea5e9, #10b981)', color: 'white' }}>
            <FontAwesomeIcon icon={faChalkboardTeacher} style={{ marginRight: '6px' }} />
            Instructor Engine
          </Tab>
          <Tab fontSize="xs" fontWeight="600" borderRadius="lg" _selected={{ bgGradient: 'linear(to-r, #0ea5e9, #10b981)', color: 'white' }}>
            <FontAwesomeIcon icon={faUsers} style={{ marginRight: '6px' }} />
            Mobile Gamification
          </Tab>
        </TabList>

        <TabPanels mt={4}>
          {/* TAB 1: INSTRUCTOR ENGINE */}
          <TabPanel p={0}>
            <VStack spacing={6} align="stretch">
              {/* Top KPI Metrics */}
              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
                <Box bg={cardBg} p={4} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="600" color={mutedTextColor}>
                    Active Courses
                  </Text>
                  <Heading size="md" fontWeight="800" mt={1} color={useColorModeValue('gray.900', 'white')}>
                    {instructorStats.deployedCourses}
                  </Heading>
                  <Text fontSize="11px" color="#0284c7" mt={1}>
                    {instructorStats.totalCoursesWithFlashcards} Total Configured
                  </Text>
                </Box>

                <Box bg={cardBg} p={4} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="600" color={mutedTextColor}>
                    Total Quests
                  </Text>
                  <Heading size="md" fontWeight="800" mt={1} color={useColorModeValue('gray.900', 'white')}>
                    {instructorStats.totalQuests}
                  </Heading>
                  <Text fontSize="11px" color={mutedTextColor} mt={1}>
                    Interactive Quests Deployed
                  </Text>
                </Box>

                <Box bg={cardBg} p={4} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="600" color={mutedTextColor}>
                    Total Flashcards
                  </Text>
                  <Heading size="md" fontWeight="800" mt={1} color={useColorModeValue('gray.900', 'white')}>
                    {instructorStats.totalFlashcards}
                  </Heading>
                  <Text fontSize="11px" color={mutedTextColor} mt={1}>
                    Across all learning paths
                  </Text>
                </Box>

                <Box bg={cardBg} p={4} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="600" color={mutedTextColor}>
                    Media Storage
                  </Text>
                  <Heading size="md" fontWeight="800" mt={1} color={useColorModeValue('gray.900', 'white')}>
                    {instructorStats.totalStorageMB} MB
                  </Heading>
                  <Text fontSize="11px" color={mutedTextColor} mt={1}>
                    AWS S3 Clinical Assets
                  </Text>
                </Box>
              </SimpleGrid>

              {/* Chart Analytics Section with Filter Toolbar */}
              <Box bg={cardBg} p={5} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                <Flex justify="space-between" align="center" wrap="wrap" gap={3} mb={4}>
                  <Box>
                    <Heading size="sm" fontWeight="700" color={useColorModeValue('gray.900', 'white')}>
                      Quest Distribution Analytics
                    </Heading>
                    <Text fontSize="xs" color={mutedTextColor}>
                      Volume breakdown per course
                    </Text>
                  </Box>
                  <HStack spacing={2}>
                    <Select
                      size="xs"
                      borderRadius="md"
                      w="130px"
                      value={graphMetric}
                      onChange={e => setGraphMetric(e.target.value as any)}
                      bg={cardSubtleBg}
                      borderColor={borderColor}
                    >
                      <option value="quests">Metric: Quests</option>
                      <option value="flashcards">Metric: Cards</option>
                      <option value="storage">Metric: Storage</option>
                    </Select>
                    <Select
                      size="xs"
                      borderRadius="md"
                      w="110px"
                      value={graphYearFilter}
                      onChange={e => setGraphYearFilter(e.target.value as any)}
                      bg={cardSubtleBg}
                      borderColor={borderColor}
                    >
                      <option value="all">All Horizons</option>
                      <option value="2026">2026 Active</option>
                      <option value="2025">2025 Archive</option>
                    </Select>
                  </HStack>
                </Flex>

                <Box h="220px" w="100%">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={useColorModeValue('#e5e7eb', '#1f2937')} />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 11 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 11 }} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey={graphMetric}
                        name={graphMetric === 'quests' ? 'Quests' : graphMetric === 'flashcards' ? 'Flashcards' : 'Storage'}
                        fill="#10b981"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={32}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              {/* Course Flashcard Table */}
              <Box bg={cardBg} borderRadius="xl" border="1px solid" borderColor={borderColor} overflow="hidden">
                <Flex p={4} borderBottom="1px solid" borderColor={borderColor} justify="space-between" align="center" wrap="wrap" gap={3}>
                  <InputGroup size="sm" maxW="280px">
                    <InputLeftElement pointerEvents="none">
                      <FontAwesomeIcon icon={faSearch} color="#9ca3af" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search courses..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      borderRadius="lg"
                      bg={cardSubtleBg}
                      borderColor={borderColor}
                    />
                  </InputGroup>

                  <HStack spacing={2}>
                    <Button
                      size="xs"
                      variant={filterStatus === 'all' ? 'solid' : 'outline'}
                      bgGradient={filterStatus === 'all' ? 'linear(to-r, #0ea5e9, #10b981)' : undefined}
                      bg={filterStatus === 'all' ? undefined : 'transparent'}
                      color={filterStatus === 'all' ? 'white' : mutedTextColor}
                      _hover={{ bgGradient: filterStatus === 'all' ? 'linear(to-r, #0284c7, #059669)' : undefined, bg: filterStatus === 'all' ? undefined : hoverBg }}
                      onClick={() => setFilterStatus('all')}
                      borderRadius="md"
                    >
                      All
                    </Button>
                    <Button
                      size="xs"
                      variant={filterStatus === 'deployed' ? 'solid' : 'outline'}
                      bgGradient={filterStatus === 'deployed' ? 'linear(to-r, #0ea5e9, #10b981)' : undefined}
                      bg={filterStatus === 'deployed' ? undefined : 'transparent'}
                      color={filterStatus === 'deployed' ? 'white' : mutedTextColor}
                      _hover={{ bgGradient: filterStatus === 'deployed' ? 'linear(to-r, #0284c7, #059669)' : undefined, bg: filterStatus === 'deployed' ? undefined : hoverBg }}
                      onClick={() => setFilterStatus('deployed')}
                      borderRadius="md"
                    >
                      Deployed
                    </Button>
                    <Button
                      size="xs"
                      variant={filterStatus === 'undeployed' ? 'solid' : 'outline'}
                      bgGradient={filterStatus === 'undeployed' ? 'linear(to-r, #0ea5e9, #10b981)' : undefined}
                      bg={filterStatus === 'undeployed' ? undefined : 'transparent'}
                      color={filterStatus === 'undeployed' ? 'white' : mutedTextColor}
                      _hover={{ bgGradient: filterStatus === 'undeployed' ? 'linear(to-r, #0284c7, #059669)' : undefined, bg: filterStatus === 'undeployed' ? undefined : hoverBg }}
                      onClick={() => setFilterStatus('undeployed')}
                      borderRadius="md"
                    >
                      Drafts
                    </Button>
                  </HStack>
                </Flex>

                <Table variant="simple" size="sm">
                  <Thead bg={headerBg}>
                    <Tr>
                      <Th py={3} fontSize="11px" color={mutedTextColor}>Course Name</Th>
                      <Th py={3} fontSize="11px" color={mutedTextColor} isNumeric>Quests</Th>
                      <Th py={3} fontSize="11px" color={mutedTextColor} isNumeric>Total Cards</Th>
                      <Th py={3} fontSize="11px" color={mutedTextColor} isNumeric>Storage</Th>
                      <Th py={3} fontSize="11px" color={mutedTextColor}>Status</Th>
                      <Th py={3} fontSize="11px" color={mutedTextColor} isNumeric>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredCourseList.length === 0 ? (
                      <Tr>
                        <Td colSpan={6} textAlign="center" py={8} color={mutedTextColor}>
                          No courses matching criteria.
                        </Td>
                      </Tr>
                    ) : (
                      filteredCourseList.map(({ course, quests, totalCards, storageMB, isDeployed }) => (
                        <Tr key={course.id} _hover={{ bg: hoverBg }}>
                          <Td py={3}>
                            <Text fontWeight="600" fontSize="xs" color={useColorModeValue('gray.900', 'white')}>
                              {course.name || 'Untitled Course'}
                            </Text>
                            <Text fontSize="10px" color={mutedTextColor}>
                              {course.id}
                            </Text>
                          </Td>
                          <Td py={3} isNumeric fontSize="xs" fontWeight="700" color="#0284c7">
                            {quests.length}
                          </Td>
                          <Td py={3} isNumeric fontSize="xs">
                            {totalCards}
                          </Td>
                          <Td py={3} isNumeric fontSize="xs" color={mutedTextColor}>
                            {storageMB.toFixed(1)} MB
                          </Td>
                          <Td py={3}>
                            <Badge
                              bg={isDeployed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(234, 179, 8, 0.1)'}
                              color={isDeployed ? '#10b981' : '#eab308'}
                              px={2}
                              py={0.5}
                              borderRadius="md"
                              fontSize="10px"
                            >
                              {isDeployed ? 'Deployed' : 'Draft'}
                            </Badge>
                          </Td>
                          <Td py={3} isNumeric>
                            <HStack spacing={2} justify="flex-end">
                              <Button
                                size="xs"
                                variant="outline"
                                borderColor={borderColor}
                                borderRadius="md"
                                leftIcon={<FontAwesomeIcon icon={faEye} />}
                                onClick={() => handleOpenPreview(course)}
                              >
                                Preview Quest
                              </Button>
                              <Button
                                size="xs"
                                variant="ghost"
                                color="#0ea5e9"
                                _hover={{ bg: 'rgba(14, 165, 233, 0.1)' }}
                                borderRadius="md"
                                leftIcon={<FontAwesomeIcon icon={faChartBar} />}
                                onClick={() => fetchCourseAnalytics(course)}
                              >
                                Analytics
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          </TabPanel>

          {/* TAB 2: MOBILE GAMIFICATION */}
          <TabPanel p={0}>
            <VStack spacing={6} align="stretch">
              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
                <Box bg={cardBg} p={4} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="600" color={mutedTextColor}>
                    Quests Completed
                  </Text>
                  <Heading size="md" fontWeight="800" mt={1} color={useColorModeValue('gray.900', 'white')}>
                    {userStats.questsCompleted}
                  </Heading>
                  <Text fontSize="11px" color="#10b981" mt={1}>
                    Network Wide
                  </Text>
                </Box>

                <Box bg={cardBg} p={4} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="600" color={mutedTextColor}>
                    Average Accuracy
                  </Text>
                  <Heading size="md" fontWeight="800" mt={1} color={useColorModeValue('gray.900', 'white')}>
                    {userStats.rightAnswers + userStats.wrongAnswers > 0
                      ? `${Math.round((userStats.rightAnswers / (userStats.rightAnswers + userStats.wrongAnswers)) * 100)}%`
                      : '0%'}
                  </Heading>
                  <Text fontSize="11px" color={mutedTextColor} mt={1}>
                    Correct vs Total Answers
                  </Text>
                </Box>

                <Box bg={cardBg} p={4} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="600" color={mutedTextColor}>
                    Active Mobile Students
                  </Text>
                  <Heading size="md" fontWeight="800" mt={1} color={useColorModeValue('gray.900', 'white')}>
                    {userStats.activeUsers}
                  </Heading>
                  <Text fontSize="11px" color={mutedTextColor} mt={1}>
                    Unique Mobile App Users
                  </Text>
                </Box>

                <Box bg={cardBg} p={4} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="600" color={mutedTextColor}>
                    Total XP Earned
                  </Text>
                  <Heading size="md" fontWeight="800" mt={1} color={useColorModeValue('gray.900', 'white')}>
                    {userStats.totalScore.toLocaleString()}
                  </Heading>
                  <Text fontSize="11px" color={mutedTextColor} mt={1}>
                    Gamification Points
                  </Text>
                </Box>
              </SimpleGrid>

              {/* Charts */}
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <Box bg={cardBg} p={5} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                  <Heading size="sm" fontWeight="700" color={useColorModeValue('gray.900', 'white')} mb={4}>
                    Student Accuracy Ratio
                  </Heading>
                  <Flex align="center" justify="center" h="200px">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={accuracyData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={75}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {accuracyData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={ACCURACY_COLORS[index % ACCURACY_COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Flex>
                </Box>

                <Box bg={cardBg} p={5} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                  <Heading size="sm" fontWeight="700" color={useColorModeValue('gray.900', 'white')} mb={4}>
                    Student Rank & Level Distribution
                  </Heading>
                  <Flex align="center" justify="center" h="200px">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={liveDistributionData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={75}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {liveDistributionData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Flex>
                </Box>
              </SimpleGrid>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* INTERACTIVE FLASHCARD QUEST PREVIEW MODAL */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="2xl" isCentered>
        <ModalOverlay backdropFilter="blur(8px)" bg="rgba(0, 0, 0, 0.7)" />
        <ModalContent bg={cardBg} borderRadius="2xl" border="1px solid" borderColor={borderColor} overflow="hidden" shadow="2xl">
          <ModalHeader bg={cardSubtleBg} borderBottom="1px solid" borderColor={borderColor} py={3.5}>
            <Flex justify="space-between" align="center">
              <HStack spacing={2.5}>
                <Box p={2} bg="rgba(14, 165, 233, 0.1)" color="#0284c7" borderRadius="lg">
                  <FontAwesomeIcon icon={faGamepad} />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="700" color={useColorModeValue('gray.900', 'white')}>
                    {previewCourse?.name || 'Course'} Flashcard Quest Preview
                  </Text>
                  <Text fontSize="11px" color={mutedTextColor}>
                    Interactive Flip Card & Diagnostic Verification
                  </Text>
                </Box>
              </HStack>
              <ModalCloseButton position="static" />
            </Flex>
          </ModalHeader>

          <ModalBody p={5}>
            {previewQuests.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text color={mutedTextColor}>No flashcard quests available for this course.</Text>
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                {/* Quest Selector Bar */}
                <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                  <Select
                    size="sm"
                    w="260px"
                    borderRadius="lg"
                    bg={cardSubtleBg}
                    borderColor={borderColor}
                    value={previewActiveQuestIdx}
                    onChange={e => {
                      setPreviewActiveQuestIdx(Number(e.target.value))
                      setPreviewActiveCardIdx(0)
                      setIsFlipped(false)
                      setPreviewSelectedOption(null)
                      setPreviewShowFeedback(false)
                    }}
                  >
                    {previewQuests.map((q, idx) => (
                      <option key={q.id} value={idx}>
                        Quest {idx + 1}: {q.title || 'Untitled'}
                      </option>
                    ))}
                  </Select>

                  <Text fontSize="xs" fontWeight="700" color={mutedTextColor}>
                    Card {previewActiveCardIdx + 1} of {activeQuest?.flashcards?.length || 0}
                  </Text>
                </Flex>

                {/* 3D Flip Card */}
                {activeCard && (
                  <Box
                    minH="260px"
                    p={5}
                    borderRadius="xl"
                    bg={isFlipped ? 'rgba(14, 165, 233, 0.08)' : cardSubtleBg}
                    border="1px solid"
                    borderColor={isFlipped ? '#0ea5e9' : borderColor}
                    position="relative"
                    cursor="pointer"
                    onClick={() => setIsFlipped(!isFlipped)}
                    transition="all 0.25s ease"
                    _hover={{ borderColor: '#0ea5e9' }}
                  >
                    <Flex justify="space-between" align="center" mb={3}>
                      <Badge bg={isFlipped ? 'linear-gradient(135deg, #0ea5e9, #10b981)' : 'gray.600'} color="white" borderRadius="md" px={2} py={0.5} fontSize="10px">
                        {isFlipped ? 'BACK: ANSWER & RATIONALE' : 'FRONT: QUESTION / PROMPT'}
                      </Badge>
                      <Button size="xs" variant="ghost" color="#0ea5e9" leftIcon={<FontAwesomeIcon icon={faExchangeAlt} />}>
                        Flip Card
                      </Button>
                    </Flex>

                    {!isFlipped ? (
                      <VStack align="stretch" spacing={3}>
                        <Text fontSize="sm" fontWeight="700" color={useColorModeValue('gray.900', 'white')}>
                          {activeCard.text || activeCard.question || 'No card prompt provided.'}
                        </Text>

                        {activeCard.images && activeCard.images.length > 0 && (
                          <Image
                            src={activeCard.images[0]}
                            maxH="140px"
                            objectFit="contain"
                            borderRadius="lg"
                            border="1px solid"
                            borderColor={borderColor}
                          />
                        )}

                        {activeCard.options && activeCard.options.length > 0 && (
                          <VStack align="stretch" spacing={2} mt={2}>
                            {activeCard.options.map((opt: string, optIdx: number) => (
                              <Box
                                key={optIdx}
                                p={2.5}
                                borderRadius="lg"
                                bg={cardBg}
                                border="1px solid"
                                borderColor={borderColor}
                                fontSize="xs"
                                fontWeight="500"
                              >
                                <Text as="span" fontWeight="700" color="#10b981" mr={2}>
                                  {String.fromCharCode(65 + optIdx)}.
                                </Text>
                                {opt}
                              </Box>
                            ))}
                          </VStack>
                        )}
                      </VStack>
                    ) : (
                      <VStack align="stretch" spacing={3}>
                        <Box bg="rgba(16, 185, 129, 0.1)" p={3} borderRadius="lg" border="1px solid" borderColor="rgba(16, 185, 129, 0.2)">
                          <Text fontSize="xs" fontWeight="700" color="#10b981" mb={1}>
                            Correct Answer:
                          </Text>
                          <Text fontSize="sm" fontWeight="600" color={useColorModeValue('gray.900', 'white')}>
                            {typeof activeCard.correctAnswer === 'number' && activeCard.options
                              ? activeCard.options[activeCard.correctAnswer]
                              : activeCard.correctAnswer || activeCard.answer || 'Answer verified.'}
                          </Text>
                        </Box>

                        {activeCard.rationale && (
                          <Box bg={cardBg} p={3} borderRadius="lg" border="1px solid" borderColor={borderColor}>
                            <Text fontSize="xs" fontWeight="700" color={mutedTextColor} mb={1}>
                              Clinical Rationale / Explanation:
                            </Text>
                            <Text fontSize="xs" color={useColorModeValue('gray.700', 'gray.300')}>
                              {activeCard.rationale}
                            </Text>
                          </Box>
                        )}
                      </VStack>
                    )}
                  </Box>
                )}

                {/* Navigation Controls */}
                <Flex justify="space-between" align="center">
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor={borderColor}
                    leftIcon={<FontAwesomeIcon icon={faChevronLeft} />}
                    isDisabled={previewActiveCardIdx === 0}
                    onClick={() => {
                      setPreviewActiveCardIdx(p => Math.max(0, p - 1))
                      setIsFlipped(false)
                    }}
                    borderRadius="lg"
                  >
                    Previous Card
                  </Button>

                  <Button
                    size="sm"
                    colorScheme="emerald"
                    bg="#10b981"
                    color="white"
                    _hover={{ bg: '#059669' }}
                    rightIcon={<FontAwesomeIcon icon={faChevronRight} />}
                    isDisabled={!activeQuest?.flashcards || previewActiveCardIdx >= activeQuest.flashcards.length - 1}
                    onClick={() => {
                      setPreviewActiveCardIdx(p => p + 1)
                      setIsFlipped(false)
                    }}
                    borderRadius="lg"
                  >
                    Next Card
                  </Button>
                </Flex>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter bg={cardSubtleBg} borderTop="1px solid" borderColor={borderColor} py={3}>
            <Button size="sm" variant="ghost" onClick={onPreviewClose} borderRadius="lg">
              Close Preview
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Push Notification Modal */}
      <Modal isOpen={notificationModalOpen} onClose={() => setNotificationModalOpen(false)} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(8px)" bg="rgba(0, 0, 0, 0.7)" />
        <ModalContent bg={cardBg} borderRadius="2xl" border="1px solid" borderColor={borderColor} overflow="hidden">
          <ModalHeader bg={cardSubtleBg} borderBottom="1px solid" borderColor={borderColor} py={3.5}>
            <HStack spacing={2}>
              <FontAwesomeIcon icon={faPaperPlane} color="#10b981" />
              <Text fontSize="sm" fontWeight="700">Push Notification Sent</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={5}>
            <VStack spacing={4} align="stretch">
              <Box bg={cardSubtleBg} p={3.5} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                <Text fontSize="xs" color={mutedTextColor}>Recipient:</Text>
                <Text fontSize="xs" fontWeight="700" color="#10b981">
                  {notificationModalData.studentName || 'All Mobile Users'}
                </Text>
                <Text fontSize="xs" color={mutedTextColor} mt={2}>Message:</Text>
                <Text fontSize="xs" fontWeight="500">
                  {notificationModalData.message}
                </Text>
              </Box>
              <Flex bg="rgba(16, 185, 129, 0.1)" p={3} borderRadius="xl" align="center" gap={2.5}>
                <FontAwesomeIcon icon={faCheckCircle} color="#10b981" />
                <Text fontSize="xs" color="#10b981" fontWeight="600">
                  Delivered successfully to student devices via AWS SNS.
                </Text>
              </Flex>
            </VStack>
          </ModalBody>
          <ModalFooter bg={cardSubtleBg} borderTop="1px solid" borderColor={borderColor} py={3}>
            <Button size="sm" colorScheme="emerald" bg="#10b981" color="white" onClick={() => setNotificationModalOpen(false)} borderRadius="lg">
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  )
}
